<script setup>
import { ref, computed, onMounted } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import { showModal } from '@/Utils/modalBus'
import SiteAdminService from '@/services/SiteAdminService'
import { useSiteAdmin } from '@/composables/useSiteAdmin'
import { contentKind } from '@/Utils/siteContentKind'
import { slideSourceHidden } from '@/Utils/siteFeatured'
import Spinner from '@/components/ui/Spinner.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import ModalBox from '@/components/modals/ModalBox.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import FormSelect from '@/components/Atoms/FormSelect.vue'
import FormSwitch from '@/components/Atoms/FormSwitch.vue'
import StatusPill from '@/components/Atoms/StatusPill.vue'
import Alert from '@/components/Atoms/Alert.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconPlus from '@/components/icon/IconPlus.vue'
import IconEdit from '@/components/icon/IconEdit.vue'
import IconBin from '@/components/icon/IconBin.vue'
import IconChevronUp from '@/components/icon/IconChevronUp.vue'
import IconChevronDown from '@/components/icon/IconChevronDown.vue'
import SiteImageField from '@/components/Sites/SiteImageField.vue'

// Hero / featured slides on the site's home page: each points at one content
// item and clicking it always opens that item (no custom link).
const props = defineProps({
	siteId: {
		type: String,
		required: true,
	},
	site: {
		type: Object,
		required: true,
	},
})
const { notify } = useNotification()
const admin = useSiteAdmin(() => props.siteId)

const loading = ref(true)
const items = ref([])
// Slide images that did not load fall back to a placeholder, not a broken-image icon.
const brokenImages = ref({})
const imageFailed = (item) => { brokenImages.value = { ...brokenImages.value, [item.id]: item.image } }
const showImage = (item) => Boolean(item.image) && brokenImages.value[item.id] !== item.image
const loadError = ref(null)
const modalOpen = ref(false)
const saving = ref(false)
const error = ref(null)
const editing = ref(null)
const form = ref(blank())
const pendingDelete = ref(null)
const reordering = ref(false)

function blank() {
	return { title: '', image: '', contentId: '', published: true, showUnderLiveButton: false }
}

// Only visible content can be featured (hidden items are not rendered on the
// site, so the slide would be blank); the slide being edited keeps its source
// listed even when that source has been hidden since.
const featurableContent = computed(() => admin.content.value.filter((c) => c.published !== false || (editing.value && String(editing.value.contentId) === String(c.id))))
const hiddenContentCount = computed(() => admin.content.value.filter((c) => c.published === false).length)
const contentOptions = computed(() => featurableContent.value.map((c) => ({ value: String(c.id), text: `${c.title} (${contentKind(c)})` })))
const contentTitle = (id) => admin.content.value.find((c) => String(c.id) === String(id))?.title || `#${id}`
const sourceHidden = (slide) => slideSourceHidden(slide, admin.content.value)
// "Show under the Live button" only means something for a live channel.
const selectedContent = computed(() => admin.content.value.find((c) => String(c.id) === String(form.value.contentId)) || null)
const selectedIsLive = computed(() => selectedContent.value?.type === 'live')
const canSubmit = computed(() => form.value.title.trim() && form.value.image.trim() && form.value.contentId && !saving.value)

const load = async () => {
	try {
		const res = await SiteAdminService.listFeatured(props.siteId)
		items.value = res?.items || []
		loadError.value = null
	} catch (err) {
		loadError.value = err
	}
}

onMounted(async () => {
	loading.value = true
	await Promise.all([load(), admin.loadContent()])
	loading.value = false
})

const openCreate = () => { editing.value = null; form.value = blank(); error.value = null; modalOpen.value = true }
const openEdit = (item) => {
	editing.value = item
	form.value = {
		title: item.title || '',
		image: item.image || '',
		contentId: item.contentId ? String(item.contentId) : '',
		published: item.published !== false,
		showUnderLiveButton: Boolean(item.showUnderLiveButton),
	}
	error.value = null
	modalOpen.value = true
}

const save = async () => {
	if (!canSubmit.value) return
	saving.value = true
	error.value = null
	const payload = {
		title: form.value.title.trim(),
		image: form.value.image.trim(),
		contentId: Number(form.value.contentId),
		published: form.value.published,
		showUnderLiveButton: selectedIsLive.value && form.value.showUnderLiveButton,
	}
	try {
		if (editing.value) await SiteAdminService.updateFeatured(props.siteId, editing.value.id, payload)
		else await SiteAdminService.createFeatured(props.siteId, payload)
		notify({ group: 'success', text: editing.value ? 'Featured item updated.' : 'Featured item added.' })
		modalOpen.value = false
		await load()
	} catch (err) {
		error.value = err.message || 'Could not save the featured item.'
	}
	saving.value = false
}

const requestDelete = (item) => { pendingDelete.value = item; showModal('delete-site-featured') }
const confirmDelete = async () => {
	if (!pendingDelete.value) return
	try {
		await SiteAdminService.deleteFeatured(props.siteId, pendingDelete.value.id)
		notify({ group: 'success', text: 'Featured item removed.' })
		await load()
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Could not remove the featured item.' })
	}
	pendingDelete.value = null
}

const move = async (index, delta) => {
	const target = index + delta
	if (target < 0 || target >= items.value.length || reordering.value) return
	const ids = items.value.map((i) => i.id)
	const [moved] = ids.splice(index, 1)
	ids.splice(target, 0, moved)
	reordering.value = true
	try {
		const res = await SiteAdminService.reorderFeatured(props.siteId, ids)
		if (res?.items) items.value = res.items
		else await load()
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Could not reorder.' })
	}
	reordering.value = false
}
</script>

<template>
	<div>
		<div class="mb-4 flex flex-wrap items-center gap-3">
			<div class="min-w-0 flex-1 basis-72">
				<h2 class="mb-0.5 text-headline font-semibold text-surface-text-2">Featured</h2>
				<p class="mb-0 text-tiny text-surface-7">Hero slides at the top of the home page. Each slide opens one content item.</p>
			</div>
			<FormButton size="md" class="shrink-0" :disabled="!admin.content.value.length" @click="openCreate">
				<icon-base class="mr-1.5" :width="14" :height="14"><icon-plus /></icon-base>
				Add slide
			</FormButton>
		</div>

		<Alert v-if="loadError" type="error" class="mb-4">{{ loadError.message }}</Alert>

		<Spinner v-if="loading" text="Loading featured…" classes="text-surface-8 mt-24" spinner-color="var(--c-dark-8)" spinner-size="15px" />

		<EmptyState v-else-if="!items.length" title="No featured slides" :subtitle="admin.content.value.length ? 'Pick a content item and a hero image to feature it on the home page.' : 'Add content first, then feature it here.'">
			<template v-if="admin.content.value.length" #actions>
				<FormButton class="w-64 justify-center" @click="openCreate">Add slide</FormButton>
			</template>
		</EmptyState>

		<ul v-else class="m-0 flex list-none flex-col gap-2 p-0">
			<li v-for="(item, index) in items" :key="item.id" class="rounded-lg bg-surface-2">
				<div class="flex items-center gap-3 px-3.5 py-3">
					<div class="flex shrink-0 flex-col">
						<button type="button" class="text-surface-7 hover:text-white disabled:opacity-30" :disabled="index === 0 || reordering" title="Move up" @click="move(index, -1)">
							<icon-base :width="14" :height="14"><icon-chevron-up /></icon-base>
						</button>
						<button type="button" class="text-surface-7 hover:text-white disabled:opacity-30" :disabled="index === items.length - 1 || reordering" title="Move down" @click="move(index, 1)">
							<icon-base :width="14" :height="14"><icon-chevron-down /></icon-base>
						</button>
					</div>
					<img v-if="showImage(item)" :src="item.image" alt="" class="h-11 w-20 shrink-0 rounded object-cover bg-surface-3" @error="imageFailed(item)">
					<div v-else-if="item.image" class="grid h-11 w-20 shrink-0 place-items-center rounded bg-surface-3 text-tiny uppercase text-surface-7" title="The image could not be loaded">image</div>
					<div class="min-w-0 flex-1">
						<div class="flex flex-wrap items-center gap-2">
							<h3 class="mb-0 truncate text-sm font-medium text-white">{{ item.title }}</h3>
							<StatusPill v-if="item.published === false" pill size="sm" variant="muted">Hidden</StatusPill>
							<StatusPill v-if="sourceHidden(item)" pill size="sm" variant="warning" :data-testid="`featured-source-hidden-${item.id}`" title="The content this slide opens is hidden, so the site skips the slide. Show the item under Content › Library or pick another one.">Source hidden — not shown on the site</StatusPill>
						</div>
						<p class="mb-0 truncate text-tiny text-surface-7">Opens {{ item.content?.title || contentTitle(item.contentId) }}</p>
					</div>
					<div class="flex shrink-0 items-center gap-1">
						<FormButton is-icon type="secondary" size="sm" title="Edit" @click="openEdit(item)">
							<icon-base :width="16" :height="16" fill="none"><icon-edit /></icon-base>
						</FormButton>
						<FormButton is-icon type="danger" size="sm" title="Delete" @click="requestDelete(item)">
							<icon-base :width="16" :height="16" fill="none"><icon-bin /></icon-base>
						</FormButton>
					</div>
				</div>
			</li>
		</ul>

		<ModalBox v-model="modalOpen" :title="editing ? 'Edit slide' : 'Add slide'" size="sm" scroll-body wrapper-classes="lg:h-auto lg:max-h-[85vh]">
			<form class="flex flex-col gap-4 px-4 pb-4 sm:px-6 sm:pb-6" @submit.prevent="save">
				<Alert v-if="error" type="error" class="!mb-0">{{ error }}</Alert>
				<FormInput id="featured-title" v-model="form.title" label="Title" placeholder="e.g. Opening night" input-classes="placeholder:text-surface-6" :maxlength="255" />
				<SiteImageField id="featured-image" v-model="form.image" :site-id="siteId" label="Hero image" hint="JPEG or PNG, 1920×1080 px (16:9)" />
				<div class="min-w-0">
					<FormSelect id="featured-content" v-model="form.contentId" label="Opens content" :options="[{ value: '', text: 'Choose content…' }, ...contentOptions]" />
					<p v-if="hiddenContentCount" class="mb-0 mt-1.5 text-tiny text-surface-7" data-testid="featured-hidden-hint">
						{{ hiddenContentCount }} hidden {{ hiddenContentCount === 1 ? 'item is' : 'items are' }} not listed — only visible content can be featured. Show it under Content › Library first.
					</p>
					<p v-if="editing && selectedContent && selectedContent.published === false" class="mb-0 mt-1.5 text-tiny text-warning" data-testid="featured-selected-hidden">
						This item is hidden on the site, so the slide is not shown until it is visible again.
					</p>
				</div>
				<FormSwitch id="featured-published" v-model="form.published" :label="form.published ? 'Visible' : 'Hidden'" />
				<div v-if="selectedIsLive">
					<FormSwitch id="featured-live-btn" v-model="form.showUnderLiveButton" label="Show under the Live button" />
					<p class="mb-0 mt-1.5 text-tiny text-surface-7" data-testid="featured-live-btn-hint">
						Keeps this live content in the site's Live menu even when it is not on air or scheduled within 24 hours. The menu lists each live content once — with two or more entries the Live button opens a dropdown, with one it goes straight to the stream.
					</p>
				</div>
			</form>
			<template #footer>
				<footer class="flex flex-col gap-2 border-t border-surface-3 p-4 sm:flex-row-reverse sm:p-6">
					<FormButton class="w-full justify-center sm:w-auto" :disabled="!canSubmit" :is-loading="saving" @click="save">{{ editing ? 'Save' : 'Add slide' }}</FormButton>
					<FormButton class="w-full justify-center sm:w-auto" type="secondary" @click="modalOpen = false">Cancel</FormButton>
				</footer>
			</template>
		</ModalBox>

		<ConfirmModal modal-id="delete-site-featured" modal-type="danger" ok-text="Remove" cancel-text="Cancel"
			title="Remove this slide?" :message="`&quot;${pendingDelete?.title || ''}&quot; disappears from the home page hero. The content itself stays.`"
			@modal-confirm="confirmDelete" />
	</div>
</template>
